import Dexie from 'dexie';
import levenshtein from 'js-levenshtein';
import { escapeRegExp } from 'lodash';

import { isTruthy } from '../../../utils/misc';

export const db = new Dexie('osmosis_data');

/* ========== Definitions ========== */
db.version(6).stores({
  databaseMeta: '[meta+identifier]',

  searchEngine:
    '&sG5ArticleCode, sG5ArticleManufCode, sG5ArticleLibelle, sCodeLPP, sCodeCIP, sCodeEAN',

  familiesByCustomer: '&sCode, sRayon',

  productsByCustomer:
    '&sCode, sLibelle, sFamille, sCodeTypeArticle, sManufacturerRef, sCodeFournisseur, sCodeCIP, sCodeEAN, sCodeGTIN, sCodeLPP',

  euroNews:
    '&kIDEuroNews, sTitle, sTextOrHtml, iImagePosition, vImageLocalName, sImageWebPath, dDateFrom, dDateTo, sTag',
});

db.version(5).stores({
  searchEngine:
    '&sG5ArticleCode, sG5ArticleManufCode, sG5ArticleLibelle, sCodeLPP, sCodeCIP, sCodeEAN',

  familiesByCustomer: '&sCode, sRayon',

  productsByCustomer:
    '&sCode, sLibelle, sFamille, sCodeTypeArticle, sManufacturerRef, sCodeFournisseur, sCodeCIP, sCodeEAN, sCodeGTIN, sCodeLPP',

  euroNews:
    '&kIDEuroNews, sTitle, sTextOrHtml, iImagePosition, vImageLocalName, sImageWebPath, dDateFrom, dDateTo, sTag',
});

db.version(4).stores({
  searchEngine:
    '&sG5ArticleCode, sG5ArticleManufCode, sG5ArticleLibelle, sCodeLPP, sCodeCIP, sCodeEAN',

  familiesByCustomer: '&sCode, sRayon',

  productsByCustomer:
    '&sCode, sLibelle, sFamille, sCodeTypeArticle, sManufacturerRef, sCodeFournisseur, sCodeCIP, sCodeEAN, sCodeGTIN, sCodeLPP',
});

db.version(3).stores({
  searchEngine:
    '&sG5ArticleCode, sG5ArticleManufCode, sG5ArticleLibelle, sCodeLPP, sCodeCIP, sCodeEAN',

  familiesByCustomer: '&sCode, sRayon',

  productsByCustomer: '&sCode, sFamille',
});

db.version(2).stores({
  searchEngine:
    '&sG5ArticleCode, sG5ArticleManufCode, sG5ArticleLibelle, sCodeLPP, sCodeCIP, sCodeEAN',

  familiesByCustomer: '&sCode, sRayon',
});

db.version(1).stores({
  searchEngine: '&sG5ArticleCode',
});

/* ========== Functions ========== */
// → Meta
export const idbPutMeta = async (meta, identifier, value) => {
  await db.databaseMeta.put({ meta, identifier, value });
};

export const idbGetMeta = async (meta, identifier) => {
  return (await db.databaseMeta.where({ meta, identifier }).first())?.value;
};

// → Search engine
export const idbPutSearchEngineRecords = async records => {
  await db.searchEngine.clear();
  await db.searchEngine.bulkPut(records);
};

export const idbQueryToFuzzyRegex = query => {
  const search = escapeRegExp(query || '')
    .trim()
    .replace(/\s+/g, '.+');

  return new RegExp(`.*${search}.*`, 'i');
};

export const idbQuerySearchEngine = async search => {
  const reg = idbQueryToFuzzyRegex(search);

  const records = await db.searchEngine
    .filter(
      ({
        sG5ArticleCode,
        sG5ArticleManufCode,
        sG5ArticleLibelle,
        sCodeLPP,
        sCodeCIP,
        sCodeEAN,
      }) =>
        reg.test(sG5ArticleCode || '') ||
        reg.test(sG5ArticleManufCode || '') ||
        reg.test(sG5ArticleLibelle || '') ||
        reg.test(sCodeLPP || '') ||
        reg.test(sCodeCIP || '') ||
        reg.test(sCodeEAN || ''),
    )
    .toArray();

  return records.sort(
    (
      { sG5ArticleLibelle: a1, sG5ArticleCode: a2 },
      { sG5ArticleLibelle: b1, sG5ArticleCode: b2 },
    ) =>
      levenshtein(a1, search) - levenshtein(b1, search) ||
      levenshtein(a2, search) - levenshtein(b2, search),
  );
};

export const idbHasSearchEngine = async () => {
  const count = await db.searchEngine.count();

  return count > 0;
};

// → FamiliesByCustomer
export const idbPutFamiliesByCustomerRecords = async (records, customerID) => {
  console.debug('📥 putFamiliesByCustomerRecords');

  await db.familiesByCustomer.clear();
  await db.familiesByCustomer.bulkPut(records);

  await idbPutMeta('updated_at', 'familiesByCustomer', Date.now());

  if (customerID) {
    await idbPutMeta('customer_id', 'familiesByCustomer', customerID);
  }
};

export const idbFamiliesCount = async () => db.familiesByCustomer.count();

export const idbGetAllFamiliesByCustomer = async () =>
  db.familiesByCustomer.toArray();

export const idbGetFamiliesByAisle = async aisleCode =>
  db.familiesByCustomer.where({ sRayon: aisleCode }).toArray();

export const idbGetSingleFamily = async familyCode =>
  db.familiesByCustomer.get(familyCode);

export const idbClearAllFamilies = async () => db.familiesByCustomer.clear();

export const idbHasFamilies = async () => (await idbFamiliesCount()) > 0;

// → ProductsByCustomer
export const idbPutProductsByCustomerRecords = async records => {
  console.debug('📥 putProductsByCustomerRecords');

  await db.productsByCustomer.clear();
  await db.productsByCustomer.bulkPut(records);
};

/**
 * @warn Potentially very slow and memory heavy operation
 */
export const idbGetAllProductsByCustomer = async () =>
  db.productsByCustomer.toArray();

export const idbGetProductsByFamily = async familyCode =>
  db.productsByCustomer.where({ sFamille: familyCode }).toArray();

export const idbClearAllProducts = async () => db.productsByCustomer.clear();

export const idbGetSingleProduct = async code =>
  db.productsByCustomer.get(code);

export const idbSearchProducts = async args => {
  const { sSearch } = args;
  const search = escapeRegExp(sSearch.toString()).toLocaleLowerCase();

  const re = new RegExp(`.*${search}.*`);

  const allMatchingProducts = await db.productsByCustomer
    .filter(product =>
      [
        'sLibelle',
        'sCodeTypeArticle',
        'sManufacturerRef',
        'sCodeFournisseur',
        'sCodeCIP',
        'sCodeEAN',
        'sCodeGTIN',
        'sCodeLPP',
      ]
        .map(prop => re.test(product[prop]?.toString()?.toLocaleLowerCase()))
        .some(test => isTruthy(test)),
    )
    .toArray();

  return allMatchingProducts;
};

// → euroNews
export const idbPutEuronewsRecords = async records => {
  const recordsData = (() => {
    if (!records) {
      return [];
    }

    if (!Array.isArray(records)) {
      return [records];
    }

    return records;
  })();

  await db.euroNews.clear();
  await db.euroNews.bulkPut(recordsData);
};

export const idbGetAllEuroNews = async () => db.euroNews.toArray();
